import styles from "~/sass/organisms/Article/Article.module.scss";
import Head from "~/components/Meta/Head";
import ArticleAuthor from "~/components/molecules/Article/ArticleAuthor.js";
import Script from "next/script";

export default function NativeAmericanHistoryArticle({}) {
  const content = () => {
    return (
      <>
        <h1 className={styles["Article__heading"]}>
          Homes and Dwellings: Native American History for Kids
        </h1>
        <ArticleAuthor />
        <p>
          Native Americans are part of different tribes, and these tribes have
          each built and lived in very different kinds of homes. Native
          Americans, like all people still do, built homes to fit their
          lifestyles. The kind of home they traditionally built also depended on
          what resources they had available and the weather of the area where
          they lived. For instance, a home that was perfect for cold, snowy
          conditions wasn't right for a home in the desert.
        </p>
        <h2 className={styles["Article__sub-heading"]}>Lifestyle</h2>
        <p>
          Different tribes had very different lifestyles. The tribes that lived
          in the{" "}
          <a
            rel="nofollow"
            href="https://nebraskastudies.org/en/1850-1874/native-american-settlers/na-meet-the-challenges/"
          >
            Great Plains
          </a>{" "}
          tended to be nomads, meaning that an entire village would travel
          together, usually as they followed the{" "}
          <a
            rel="nofollow"
            href="https://www.legendsofamerica.com/na-plainsindians/#:~:text=The%20Arapaho%2C%20Assiniboine%2C%20Blackfoot%2C,herds%20and%20lived%20in%20tipis."
          >
            buffalo
          </a>{" "}
          they hunted for food. The tepee was developed by nomadic people
          because they needed homes that were easy to transport when they moved
          on to their next location. Other tribes settled in places that were
          close to easily accessible sources of food and water. These tribes
          built permanent structures for their homes.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          Native American Tepee
        </h2>
        <p>
          Tepees are also known as tipis and teepees. They are very efficient
          portable homes. The typical Native American woman could set up a tepee
          for her family in about half an hour. The frame of a tepee consists of
          long rods that are far apart at the bottom but tied very tightly
          together at the top. The outside of the tepee was usually a very large
          buffalo hide. The snugness of the tepee meant that it would stay warm
          in the winter once everyone was inside and a fire was built in the
          middle. In the summer, the hide would be tied up so that air would
          flow into the bottom of the tepee.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          Native American Longhouse
        </h2>
        <p>
          In the Northeast, the people of the{" "}
          <a
            rel="nofollow"
            href="https://www.haudenosauneeconfederacy.com/who-we-are/"
          >
            Haudenosaunee Confederacy
          </a>
          , also known as the Iroquois, lived in longhouses. In fact,
          "Haudenosaunee" translates as "people of the longhouse." The
          Haudenosaunee people needed structures that could withstand harsh
          winters. Longhouses are permanent structures that are constructed in
          the shape of a rectangle with very long sides. The typical longhouse
          was about 18 feet wide and 80 feet long and was built out of wood and
          tree bark. The structure of the roof consisted of curved rods carved
          from local trees, and bark was laid across the roof and walls in an
          overlapping pattern, much like shingles are used today. The bark
          siding protected the inside of the building from snow, rain, and wind.
          Holes in the roof provided ventilation, and usually, there was a door
          at each end of a longhouse. Villages typically had many longhouses
          grouped together inside of a palisade, which is a type of fence. Each
          longhouse could be the home of as many as 20 people.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          Native American Pueblo
        </h2>
        <p>
          The{" "}
          <a rel="nofollow" href="https://www.hopi-nsn.gov/tribal-government/">
            Hopi
          </a>{" "}
          tribe, who lived in the Southwest, built and lived in pueblos. These
          were large structures, usually built into the sides of cliffs or
          inside of caves from adobe clay bricks. The Hopis made the{" "}
          <a
            rel="nofollow"
            href="https://www.solidearth.co.nz/earthbuilding-information/building-with-adobe-brick-technique/"
          >
            bricks
          </a>{" "}
          by mixing straw, grass, and sand with clay before letting them harden
          in the sunlight. After building walls from the bricks, the Hopi would
          then cover each wall with more adobe clay to strengthen it. Each year,
          they would add a new layer of clay. The{" "}
          <a href="https://www.realestateagents.com/az/phoenix">homes</a> were
          constructed vertically, with up to five rooms built of adobe clay
          bricks stacked on top of each other. The largest room would be on the
          bottom and the smallest at the top.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          Native American Hogan
        </h2>
        <p>
          Another Southwestern tribe, the{" "}
          <a rel="nofollow" href="https://www.ihs.gov/navajo/navajonation/">
            Navajo
          </a>
          , built and lived in hogans. Hogans were also built using adobe clay,
          but they were very different from pueblos. The frame of each home was
          built out of wooden poles in the shape of a dome. It was then covered
          in adobe. The doors on hogans faced east, in the direction of the
          rising sun. A hole in the roof provided a way to vent the smoke from
          the fires the family would light inside the home.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          Native American Wigwam
        </h2>
        <p>
          Algonquian tribes in what today is considered New England lived in
          homes called wigwams. Like their neighbors, the Haudenosaunee, their
          homes were covered in bark and built out of local trees. However,
          wigwams are smaller and easier to build than longhouses. Wigwams have
          a dome-like structure made out of trees that were bent into shape and
          tied together. The outside of the structure was then covered in bark.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          Other Native American Homes
        </h2>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="https://www.semtribe.com/stof/culture/chickee"
            >
              Chickee
            </a>
            : The Seminole tribe of Florida built homes with a thatched roof to
            protect inhabitants from rain but left the sides open due to
            Florida's hot temperatures.
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.nytimes.com/2017/06/21/world/canada/igloos-inuit-arctic-canada.html"
            >
              Igloos
            </a>
            : Inuits built small homes with domed roofs out of blocks of ice.
            The interior of these homes protected the Inuit from frigid northern
            winters.
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.oregonencyclopedia.org/articles/chinookan_plank_houses/"
            >
              Plankhouses
            </a>
            : These homes were built out of cedar and meant to house several
            families. They were built along the Northwest coast.
          </li>
          <li>
            <a rel="nofollow" href="https://friendsofgastateparks.org/node/787">
              Wattle and Daub Huts
            </a>
            : These{" "}
            <a href="https://www.realestateagents.com/ga/atlanta">homes</a> were
            built in areas like modern-day Georgia and North Carolina by the
            Cherokee tribe. They were similar to chickees but had side walls and
            were insulated with clay.
          </li>
        </ul>
        <h2>Additional Resources</h2>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="https://www.beachwoodschools.org/Downloads/Native%20Binder.pdf"
            >
              Native American Life
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://guides.loc.gov/native-american-spaces/cartographic-resources/indian-sites"
            >
              Indian Lands, Village Sites, Tribal Range, Place Names, and
              Communication Routes
            </a>
          </li>
          <li>
            <a rel="nofollow" href="http://www.native-languages.org/houses.htm">
              Native American Houses
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.aaanativearts.com/types-of-ancient-native-american-homes"
            >
              Types of Ancient Native American Homes
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.enchantedlearning.com/history/us/nativeamerican/housing/index.shtml"
            >
              Dwellings of Native Americans
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="http://plainshumanities.unl.edu/encyclopedia/doc/egp.arc.048"
            >
              Encyclopedia of the Great Plains: Tepees
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.youtube.com/watch?v=jNC2V3ekuRo"
            >
              Native Americans Tepee, Longhouse, and Pueblo Homes
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="http://www.nysm.nysed.gov/exhibitions/ongoing/native-peoples-new-york/mohawk-longhouse"
            >
              Mohawk Haudenosaunee (Iroquois) Longhouse
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.crowcanyon.org/educationproducts/pueblo_history_kids/introduction.asp"
            >
              Pueblo Indian History for Kids
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.visitalbuquerque.org/media/media-kit/pueblos/"
            >
              Native American Pueblos
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.texasbeyondhistory.net/kids/houses/pueblos.html"
            >
              Pueblos: Community Houses
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.nps.gov/petr/planyourvisit/pueblos.htm"
            >
              Visiting New Mexico Pueblos
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://chambersarchitects.com/blog/pueblo-architecture-taos-new-mexico/"
            >
              Pueblo Architecture of Northern New Mexico
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://online.nmartmuseum.org/nmhistory/art-architecture/ancestral-pueblo-architecture/history-ancestral-pueblo-architecture.html"
            >
              History: Ancestral Pueblo Architecture
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="http://navajopeople.org/blog/navajo-homes-hogans/"
            >
              Navajo Homes: Hogans
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.architecturaldigest.com/story/navajo-hogan-eco-friendly"
            >
              Have You Heard of a Navajo Hogan?
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://wonderopolis.org/wonder/what-is-a-wigwam"
            >
              What Is a Wigwam?
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://campuspress.yale.edu/yipp/inside-a-connecticut-indian-wigwam/"
            >
              Inside a Connecticut Indian Wigwam
            </a>
          </li>
        </ul>
      </>
    );
  };

  return (
    <>
      <Head
        title={
          "Native American History for Kids: Homes and Dwellings | RealEstateAgents.com"
        }
        description={
          "Details and description about the types of homes traditionally used by Native Americans, including a resource list to learn more."
        }
      />
      <article className={styles["Article"]}>
        <div className={styles["Article__container"]}>{content()}</div>
      </article>
      <Script strategy="beforeInteractive" type="application/ld+json">
        {{
          "@context": "https://schema.org",
          "@type": "Article",
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id":
              "https://www.realestateagents.com/articles/native-american-history-kids-homes-dwellings",
          },
          headline: "Native American History for Kids: Homes and Dwellings",
          author: {
            "@type": "Person",
            name: "Amber Martinez",
            jobTitle: "Education Writer",
            sameAs: [
              "https://www.realestateagents.com/articles/author/amber-martinez",
              "https://www.linkedin.com/in/amber-martinez-7096651a/",
            ],
          },
        }}
      </Script>
    </>
  );
}
